import $ from 'jquery';
import {
    convertPayPalAddressToCart
} from '@ppcp/utils';
import AbstractPayPalGateway from './AbstractPayPalGateway';

/**
 * PayPal Gateway Implementation
 *
 * Handles PayPal-specific button creation, funding sources, and styling
 */
class PayPalGateway extends AbstractPayPalGateway {

    constructor(props) {
        super(props);
        this.buttonActions = {};
        this.buttons = {};
    }

    initialize() {
        if (this.controller.context.isCheckout()) {
            this.actions.addFilter('field_population_options', this.getFieldOptions.bind(this), 10, 2);
        }
    }

    getFieldOptions(options, controller) {
        if (controller === this.controller && controller.isPaymentGatewaySelected()) {
            options.skipShipping = !!this.settings.paypalAddressDisabled;
        }
        return options;
    }

    /**
     * Check if PayPal buttons should be created
     * @param {HTMLElement|NodeList} container - The container to check
     * @returns {boolean} True if buttons don't already exist
     */
    shouldCreateElement(container) {
        return !$(container).find('.paypal-buttons').length;
    }

    async canMakePayment() {
        return !!this.paypal;
    }

    /**
     * Create PayPal buttons for all eligible funding sources
     * @param {HTMLElement|NodeList} container - The container element(s) to render buttons
     * @returns {Promise<Object>} Promise that resolves with button instances keyed by funding source
     */
    async createPaymentElement(container) {
        this.buttons = {};
        this.buttonActions = {};
        const funding = this.getFunding(this.controller.context.getPage());
        const sortedFunding = this.getSortedFunding(funding);

        const promises = [];

        for (const source of sortedFunding) {
            const buttonOptions = this.getButtonOptions(source);
            const button = this.paypal.Buttons(buttonOptions);

            this.buttons[source] = button;

            if (button.isEligible()) {
                promises.push(button.render(container).catch(() => {
                }));
            }
        }

        await Promise.all(promises);

        return this.buttons;
    }

    /**
     * Get PayPal funding sources available for the current page
     * @param {string} page - Current page context (checkout, cart, product, etc.)
     * @returns {Array<string>} Array of PayPal funding sources
     */
    getFunding(page) {
        const funding = [];
        const settings = this.settings;

        if (this.controller.context.isProduct()) {
            if (this.settings?.product?.funding?.includes('paypal')) {
                funding.push(this.paypal.FUNDING.PAYPAL);
            }
            if (this.settings?.product?.funding?.includes('paylater')) {
                funding.push(this.paypal.FUNDING.PAYLATER);
            }
            if (this.settings?.product?.funding?.includes('card')) {
                funding.push(this.paypal.FUNDING.CARD);
            }
        } else {
            if (this.isFundingActive('paypal')) {
                if (settings?.paypal_sections?.includes(page)) {
                    funding.push(this.paypal.FUNDING.PAYPAL);
                }
            }
            if (this.isFundingActive('paylater')) {
                if (settings?.paylater_sections?.includes(page)) {
                    funding.push(this.paypal.FUNDING.PAYLATER);
                }
            }
            if (this.isFundingActive('card')) {
                if (settings?.credit_card_sections?.includes(page)) {
                    funding.push(this.paypal.FUNDING.CARD);
                }
            }
            if (this.isFundingActive('venmo')) {
                if (settings?.venmo_sections?.includes(page)) {
                    funding.push(this.paypal.FUNDING.VENMO);
                }
            }
        }

        return funding;
    }

    /**
     * Sort funding sources based on configured order
     * @param {Array<string>} funding - Array of funding sources
     * @returns {Array<string>} Sorted funding sources
     */
    getSortedFunding(funding) {
        const sortOrder = this.settings.buttons_order || [];
        funding.sort((a, b) => {
            const indexA = sortOrder.indexOf(a);
            const indexB = sortOrder.indexOf(b);
            return indexA < indexB ? -1 : 1;
        });
        return funding;
    }

    /**
     * Build button options for a specific funding source
     * @param {string} source - The funding source
     * @returns {Object} Complete button options for PayPal SDK
     */
    getButtonOptions(source) {
        const buttonOptions = {
            fundingSource: source,
            style: this.getButtonStyle(source),
            onInit: (data, actions) => {
                this.buttonActions[source] = actions;
                // legacy event for backwards compatability
                $(document.body).triggerHandler('wc_ppcp_on_init', [this.controller, source]);

                if (this.controller.context.isCheckout() || this.controller.context.isOrderPay()) {
                    if (this.controller.isTermsVisible()) {
                        this.controller.handleTermsClick();
                    }
                }
                if (this.controller.context.isProduct()) {
                    if (this.controller?.$paymentElement?.length && this.controller.$paymentElement.is('.disabled')) {
                        this.disablePaymentElement();
                    }
                }
            },
            onClick: (...args) => {
                return this.onClick(source);
            },
            onApprove: (data, actions) => {
                return this.onApprove(data, actions);
            },
            onCancel: (data = null) => {
                return this.onCancel(data, source);
            },
            onError: (error) => {
                this.onError(error, source);
            },
            onDestroy: () => {
                this.onDestroy(source);
            },
            createOrder: (data, actions) => {
                return this.controller.createOrder(data, actions);
            }
        };

        // Add shipping change handler if needed
        if (this.controller.needsShipping() && source !== this.paypal.FUNDING.VENMO) {
            buttonOptions.onShippingChange = (...args) => {
                return this.onShippingChange(...args);
            };
        }

        // Add vault setup token handler if needed
        if (this.settings.needsSetupToken) {
            buttonOptions.createOrder = null;
            buttonOptions.createVaultSetupToken = (...args) => {
                return this.controller.createVaultSetupToken();
            };
        }

        return buttonOptions;
    }

    async onShippingChange(data, actions) {
        const address = convertPayPalAddressToCart(data?.shipping_address || {}, true);
        const shipping_method = data?.selected_shipping_option?.id || '';
        try {
            const response = await this.controller.onShippingChange({address, shipping_method, order_id: data.orderID});
            if (response.code) {
                return actions.reject();
            } else {
                return actions.resolve();
            }
        } catch (error) {
            return actions.reject();
        }
    }

    /**
     * Called when a PayPal button is clicked.
     */
    onClick(source) {
        this.controller.onClick();
        this.controller.validate();
        $(document.body).triggerHandler('wc_ppcp_on_click', [this.controller, source]);
    }

    /**
     * Get button style configuration for a PayPal funding source
     * @param {string} source - The funding source
     * @returns {Object} Style configuration object
     */
    getButtonStyle(source) {
        let style = null;

        switch (source) {
            case this.paypal.FUNDING.PAYPAL:
                style = this.settings?.buttons?.paypal;
                break;

            case this.paypal.FUNDING.PAYLATER:
                style = this.settings?.buttons?.paylater;
                break;

            case this.paypal.FUNDING.CARD:
                style = {...this.settings?.buttons?.card};
                if (style.tagline) {
                    delete style.tagline;
                    style.layout = 'vertical';
                } else {
                    style.layout = 'horizontal';
                }
                break;

            case this.paypal.FUNDING.VENMO:
                const {color = '', label = '', ...venmoStyle} = this.settings?.buttons?.paypal;
                style = venmoStyle;
                break;
        }

        if (style?.height) {
            style.height = parseInt(style.height);
        }

        return style;
    }

    /**
     * Enable PayPal payment buttons
     */
    enablePaymentElement() {
        Object.keys(this.buttonActions || {}).forEach(key => {
            this.buttonActions[key]?.enable();
        });
    }

    /**
     * Disable PayPal payment buttons
     */
    disablePaymentElement() {
        Object.keys(this.buttonActions || {}).forEach(key => {
            this.buttonActions[key]?.disable();
        });
    }

    /**
     * Destroy PayPal payment buttons and clean up resources
     */
    destroyPaymentElement() {
        Object.keys(this.buttons || {}).forEach(key => {
            if (this.buttons[key]?.close) {
                this.buttons[key].close();
            }
        });
        this.buttons = {};
        this.buttonActions = {};
    }

    isFundingActive(type) {
        return this.settings?.funding?.includes(type);
    }
}

export {PayPalGateway};
export default PayPalGateway;
